"use client";

import { EmployerAccountActionMenu } from "@/app/employers/[officeId]/(use-shared-layout)/EmployerAccountActionMenu";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HamburgerMenuButton } from "@/components/ui/HamburgerMenuButton";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { useState } from "react";
import { MdExpandMore } from "react-icons/md";
import { InAppNotificationPopover } from "./_in-app-notifications/InAppNotificationPopover";

export const sideMenuToggleButtonId = "side-menu-toggle-button";

type Office = {
  id: string;
  name: string;
  officeCode: string;
};

const OfficeSwitcher = ({
  currentOffice,
  availableOffices,
}: {
  currentOffice: Office;
  availableOffices: Array<Omit<Office, "officeCode">>;
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const toggle = () => setIsOpened((p) => !p);
  const close = () => setIsOpened(false);

  return (
    <DropdownMenu open={isOpened}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" onClick={toggle} className="group">
          {currentOffice?.name}
          <MdExpandMore
            className="relative top-px ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-white"
        onCloseAutoFocus={close}
        onEscapeKeyDown={close}
        onPointerDownOutside={close}
        onInteractOutside={close}
        onFocusOutside={close}
      >
        {availableOffices.map((office) => (
          <DropdownMenuItem key={office.id} asChild>
            <Link href={`/${office.id}`} className="m-3 cursor-pointer">
              {office.name}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const EmployersHeader = ({
  currentOffice,
  availableOffices,
  employerName,
  sideMenuOpened,
  toggleSideMenuOpened,
  params,
}: {
  currentOffice: Office;
  availableOffices: Array<Omit<Office, "officeCode">>;
  employerName: string;
  sideMenuOpened: boolean;
  toggleSideMenuOpened: () => void;
  params: { officeId: string };
}) => {
  return (
    <div
      className={cn(
        "flex h-20 w-full flex-row items-center justify-between border-b border-b-monotone-1 p-4",
        "lg:p-6",
      )}
    >
      <div className="flex flex-row items-center gap-2 lg:gap-0">
        <HamburgerMenuButton
          id={sideMenuToggleButtonId}
          open={sideMenuOpened}
          onClick={() => {
            toggleSideMenuOpened();
          }}
          className="lg:hidden"
        />
        {availableOffices.length > 1 && (
          <OfficeSwitcher
            currentOffice={currentOffice}
            availableOffices={availableOffices}
          />
        )}
        {availableOffices.length === 1 && (
          <div className="ml-4 text-sm font-bold">{currentOffice?.name}</div>
        )}
      </div>
      <div className="flex items-center gap-4">
        <InAppNotificationPopover officeId={params.officeId} />
        <EmployerAccountActionMenu params={params} userName={employerName} />
      </div>
    </div>
  );
};
